import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styles from './ResetPassword.module.css';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validating, setValidating] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
      setLoading(false);
      return;
    }

    validateAndResetPassword();
  }, [token]);

  const validateAndResetPassword = async () => {
    try {
      setValidating(true);
      setError('');

      // First validate the token
      const validateResponse = await fetch(`/api/auth/validate-reset-token/${token}`);
      const validateData = await validateResponse.json();

      if (!validateData.valid) {
        setError(validateData.error || 'Invalid or expired reset link.');
        setLoading(false);
        return;
      }

      // If token is valid, redirect to set new password page
      navigate(`/set-new-password?token=${token}`);
      return;
    } catch (error) {
      console.error('Password reset error:', error);
      setError('An error occurred while resetting your password. Please try again.');
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <div className={styles.loadingSpinner}></div>
          <h1 className={styles.title}>
            {validating ? 'Resetting Password...' : 'Validating Reset Link...'}
          </h1>
          <p className={styles.message}>Please wait while we process your password reset.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Password Reset</h1>

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.error}>{error}</div>
            <div className={styles.actions}>
              <button onClick={() => navigate('/forgot-password')} className={styles.button}>
                Request New Reset Link
              </button>
              <button onClick={() => navigate('/login')} className={styles.buttonSecondary}>
                Back to Login
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className={styles.successContainer}>
            <div className={styles.success}>{success}</div>
            <div className={styles.checkmark}>✓</div>
            <p className={styles.redirectMessage}>Redirecting you to your dashboard...</p>
          </div>
        )}
      </div>
    </div>
  );
}
